import styled, { css } from 'styled-components/macro';

/**
 * Imported to apply custom focus- and hover-styles in child elements
 * Interaction happens on parent component
 * Parent component can be used as a selector e.g. ${ItemWrapper}:hover &
 */
import { MenuButton } from 'components/base/Menu/index.js';
import { ItemWrapper } from 'components/base/Menu/Menu';

import modularScale from 'styles/helpers/modular-scale';
import { ThemeProps } from 'types/styled';

import variables from 'styles/themes/variables';
import Box from 'components/elements/Box';
import colors from 'styles/themes/colors';

const sharedStyles = css`
  padding: ${({ theme }): string =>
    `${theme.spacing.small} ${theme.spacing.large}`};
  color: ${({ theme }): string => theme.NightBlue};
  text-transform: capitalize;
  white-space: nowrap;
`;

export const ButtonWrapper = styled.div<ThemeProps<'div'>>`
  ${sharedStyles};
  background: ${({ theme, isActiveGreen, isRoundedDropdown }): string =>
    isActiveGreen
      ? theme.AccentGreen
      : isRoundedDropdown
      ? theme.White
      : theme.Sand};
  transition: ${({ theme }): string =>
    `${theme.transitionDuration} ${theme.timingFunction} background`};
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  min-height: 20px;
  ${({ isActiveGreen, isRoundedDropdown, theme }): string =>
    isActiveGreen
      ? 'color: #f7f7f7;'
      : isRoundedDropdown
      ? `color: ${theme.AccentGreen};`
      : ''}
  ${({ minHeight }): string => (minHeight ? `min-height: ${minHeight};` : '')}
  ${({ isRoundedDropdown, theme }): any =>
    isRoundedDropdown &&
    css`
      border: 1px solid ${theme.AccentGreen};
      ${isRoundedDropdown === 'all'
        ? 'border-radius: 3px;'
        : isRoundedDropdown === 'right'
        ? 'border-top-right-radius: 3px; border-bottom-right-radius: 3px;'
        : 'border-top-left-radius: 3px; border-bottom-left-radius: 3px;'}
    `}

  .dropdown-chevron {
    ${({ theme, isOpen, isActiveGreen }): any => css`
      width: ${theme.spacing.medium};
      height: ${theme.spacing.small};
      margin-left: ${theme.spacing.small};
      color: ${isActiveGreen ? '#f7f7f7' : theme.AccentGreen};
      transition: transform 200ms;
      transform: ${isOpen ? 'rotate(270deg)' : 'rotate(90deg)'};
      transition-timing-function: ${theme.timingFunction};
    `}
    &.rotated {
      transform: ${({ isOpen }): string =>
        isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    }
  }

  &[disabled] {
    background: ${({ theme }): string => theme.LightGrey};
    color: ${({ theme }): string => theme.CasperGrey};
    cursor: not-allowed;

    .dropdown-chevron {
      color: currentColor;
    }
  }

  ${MenuButton}:hover &, ${MenuButton}:focus & {
    background: ${({ theme, isActiveGreen, isRoundedDropdown }): string =>
      isActiveGreen
        ? theme.AccentGreen
        : isRoundedDropdown
        ? '#f3fdfb'
        : theme.LightGrey};
    color: ${({ theme, isActiveGreen, isRoundedDropdown }): any =>
      !isActiveGreen && isRoundedDropdown ? theme.AccentGreen : theme.Sand};
    ${({ isRoundedDropdown }): string =>
      isRoundedDropdown ? 'border: 1px solid #85ebd3;' : ''}
    svg {
      color: ${({ theme, isActiveGreen, isRoundedDropdown }): any =>
        !isActiveGreen && isRoundedDropdown ? theme.AccentGreen : theme.Sand};
    }
  }
`;

export const Item = styled.div<ThemeProps<'div'>>`
  ${sharedStyles};
  ${({ isActive, theme, isInRow }): any => css`
    font-weight: ${isActive ? 600 : 'normal'};
    background: ${isActive ? theme.Sand : 'white'};
    cursor: default;
    display: flex;
    flex-direction: ${isInRow ? 'row' : 'column'};

    [disabled] & {
      color: #8ea6be;
      background: none !important;
      opacity: 0.5;
      span {
        transform: none !important;
      }
    }

    span {
      display: inline-block;
      transition: 150ms ${theme.timingFunction} transform;
    }

    ${ItemWrapper}:hover &,
    ${ItemWrapper}[aria-selected='true'] & {
      background: ${theme.Sand};
      span {
        transform: ${!isActive && 'translateX(5px)'};
      }
    }
  `}
`;

export const ButtonContent = styled.span`
  flex: 1 1 auto;
`;

export const Placeholder = styled.span`
  font-weight: normal;
  color: ${({ theme }): string => theme.CasperGrey};
`;

export const SubLabel = styled.span<ThemeProps<'span'>>`
  font-size: ${(p: any): string => p.small && modularScale(-1)};
  font-weight: normal;
  color: ${(p: any): string => p.theme.CasperGrey};
  text-align: left;
`;

export const StyledListLabel = styled(SubLabel)`
  display: block;
  background: white;
  text-transform: uppercase;
  padding: ${({ theme }): string =>
    `${theme.spacing.small} ${theme.spacing.large}`};
`;

export const ListItemBoxBase = css`
  background: ${({ theme }): string => theme.White};
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  align-content: stretch;
  padding: ${({ theme }): string =>
    `${theme.spacing.xsmall} ${theme.spacing.xsmall} ${theme.spacing.xsmall} ${theme.spacing.small}`};
  min-width: 270px;

  span {
    display: inline-block;
  }
`;

export const ListItemBox = styled(Box)<ThemeProps<'div'>>`
  ${ListItemBoxBase}

  span {
    text-transform: uppercase;
    color: ${({ theme }): string => theme.CasperGrey};
    font-size: ${modularScale(-1)};
    letter-spacing: 0.1em;
  }
`;

export const ListItemBoxWithHover = styled(Box)<ThemeProps<'div'>>`
  ${ListItemBoxBase}
  cursor: pointer;

  span {
    transition: ${`150ms ${variables.timingFunction} transform`};
  }

  &:hover,
  &[aria-selected='true'] {
    background: ${colors.Sand};
  }

  ${({ isActive }): any => css`
    cursor: pointer;
    background: ${isActive ? colors.Sand : 'white'};

    span {
      display: inline-block;
      transition: ${`150ms ${variables.timingFunction} transform`};
    }

    &:hover,
    &[aria-selected='true'] {
      span {
        transform: ${({ theme }): false | string =>
          !isActive && `translateX(${theme.spacing.xsmall})`};
      }
    }
  `}
`;
